import dayjs from "dayjs";
import {
  AssignmentKind,
  AssignmentRule,
  AssignmentTraceDTO,
  CandidateStatus,
  NextAssignmentDTO,
  RankingFactor,
} from "../../HubSpot/useGetSchedulerAssignments.ts";

export type AssignmentOutcome =
  | "normal"
  | "requested"
  | "cap_skipped"
  | "tier1_overflow"
  | "last_resort";

export const OUTCOME_ORDER: AssignmentOutcome[] = [
  "last_resort",
  "tier1_overflow",
  "cap_skipped",
  "requested",
  "normal",
];

export const OUTCOME_LABELS: Record<AssignmentOutcome, string> = {
  normal: "Il migliore libero",
  requested: "Scelto a mano",
  cap_skipped: "Primo in classifica saltato per cap",
  tier1_overflow: "Tutti al cap: Tier 1 oltre il cap",
  last_resort: "Tutti al cap, nessun Tier 1",
};

export const OUTCOME_CELL_CLASS_NAMES: Record<AssignmentOutcome, string> = {
  normal: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  requested: "bg-violet-100 text-violet-800 hover:bg-violet-200",
  cap_skipped: "bg-amber-200 text-amber-900 hover:bg-amber-300",
  tier1_overflow: "bg-orange-300 text-orange-950 hover:bg-orange-400",
  last_resort: "bg-red-300 text-red-950 hover:bg-red-400",
};

export const KIND_LABELS: Record<AssignmentKind, string> = {
  booking: "Prenotazione",
  reschedule: "Riprogrammazione",
  pending_exit: "Uscita dal pending",
  reassign: "Riassegna",
};

export const CHANNEL_LABELS: Record<AssignmentTraceDTO["channel"], string> = {
  web: "Landing",
  setter: "Setter",
};

export const RULE_LABELS: Record<AssignmentRule, string> = {
  under_capacity: "Il migliore fra chi è sotto il cap",
  tier1_overflow: "Tutti al cap: va al miglior Tier 1, oltre il cap",
  last_resort: "Tutti al cap e nessun Tier 1: il migliore libero",
  requested: "Scelto a mano dal setter",
};

export const STATUS_LABELS: Record<CandidateStatus, string> = {
  chosen: "Scelto",
  ranked_lower: "Libero, dopo in classifica",
  cap_reached: "Cap raggiunto",
  busy_google: "Occupato su Google",
  busy_scheduler: "In un'altra call dello scheduler",
};

export const STATUS_CLASS_NAMES: Record<CandidateStatus, string> = {
  chosen: "bg-green-100 text-green-800",
  ranked_lower: "bg-slate-100 text-slate-700",
  cap_reached: "bg-amber-100 text-amber-800",
  busy_google: "bg-gray-100 text-gray-500",
  busy_scheduler: "bg-gray-100 text-gray-500",
};

export const LOST_ON_LABELS: Record<RankingFactor, string> = {
  trial: "in prova, va in coda",
  cr: "CR più basso",
  tier: "tier più basso",
  round_robin: "turno del round robin",
};

export function outcomeOf(trace: AssignmentTraceDTO): AssignmentOutcome {
  if (trace.rule === "requested") return "requested";
  if (trace.rule === "tier1_overflow") return "tier1_overflow";
  if (trace.rule === "last_resort") return "last_resort";
  const ideal = trace.candidates.find(
    (c) => c.closerId === trace.idealCloserId,
  );
  return ideal &&
    ideal.closerId !== trace.chosenCloserId &&
    ideal.status === "cap_reached"
    ? "cap_skipped"
    : "normal";
}

export function mostNotableOutcome(
  traces: AssignmentTraceDTO[],
): AssignmentOutcome {
  const outcomes = new Set(traces.map(outcomeOf));
  return OUTCOME_ORDER.find((o) => outcomes.has(o)) ?? "normal";
}

const FREE_STATUSES: CandidateStatus[] = [
  "chosen",
  "ranked_lower",
  "cap_reached",
];

export function freeCandidatesOf(
  trace: AssignmentTraceDTO,
): AssignmentTraceDTO["candidates"] {
  return trace.candidates.filter((c) => FREE_STATUSES.includes(c.status));
}

function nextAssignmentLabel(next: NextAssignmentDTO): string {
  const closer = next.closerName ?? next.closerId;
  const when = dayjs(next.slotStart).tz("Europe/Rome").format("DD/MM HH:mm");
  if (next.kind === "reassign") return `Poi riassegnata a ${closer}`;
  if (next.kind === "pending_exit") {
    return `Poi uscita dal pending: ${when} · ${closer}`;
  }
  return `Poi riprogrammata: ${when} · ${closer}`;
}

export function stateLabelOf(trace: AssignmentTraceDTO): string {
  switch (trace.state.kind) {
    case "current":
      return "Attuale";
    case "superseded":
      return nextAssignmentLabel(trace.state.next);
    case "cancelled":
      return "Poi cancellata";
    case "pending":
      return "Poi in Reschedule pending";
  }
}

export function stateClassNameOf(trace: AssignmentTraceDTO): string {
  switch (trace.state.kind) {
    case "current":
      return "bg-green-100 text-green-800";
    case "superseded":
      return "bg-amber-100 text-amber-800";
    case "cancelled":
      return "bg-red-100 text-red-700";
    case "pending":
      return "bg-gray-100 text-gray-600";
  }
}

export function chosenNameOf(trace: AssignmentTraceDTO): string {
  return (
    trace.candidates.find((c) => c.closerId === trace.chosenCloserId)?.name ??
    trace.chosenCloserId
  );
}
